import { Head } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import ScreenshotCarousel from '../components/ScreenshotCarousel'
import StagingBanner from '../components/StagingBanner'
import styles from './Landing.module.css'

export default function Landing({ app_name, version, login_url, download_urls, screenshots, environment, revision, gitlab_project_url }) {
    const [userOS, setUserOS] = useState('unknown')

    useEffect(() => {
        // Detect user's operating system
        const platform = navigator.platform.toLowerCase()
        const userAgent = navigator.userAgent.toLowerCase()

        if (platform.includes('mac') || userAgent.includes('mac')) {
            setUserOS('mac')
        } else if (platform.includes('win') || userAgent.includes('windows')) {
            setUserOS('windows')
        } else if (platform.includes('linux') || userAgent.includes('linux')) {
            setUserOS('linux')
        }
    }, [])

    const getDownloadUrl = () => {
        return download_urls[userOS] || download_urls.mac_arm || '#'
    }

    const getOSDisplayName = () => {
        switch(userOS) {
            case 'mac': return 'macOS'
            case 'windows': return 'Windows'
            case 'linux': return 'Linux'
            default: return 'Desktop'
        }
    }

    const handleDownload = (url = null) => {
        const downloadUrl = url || getDownloadUrl()
        if (downloadUrl && downloadUrl !== '#') {
            window.open(downloadUrl, '_blank')
        }
    }

    const handleLogin = () => {
        window.location.href = login_url
    }

    return (
        <>
            <Head title={`${app_name} - Go Nuts for Time Tracking!`} />
            <div className={styles.landingPage}>
                {/* Animated Background Peanuts */}
                <div className={styles.backgroundPeanuts}>
                    <div className={`${styles.floatingPeanut} ${styles.peanut1}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut2}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut3}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut4}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut5}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut6}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut7}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut8}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut9}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut10}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut11}`}>🥜</div>
                    <div className={`${styles.floatingPeanut} ${styles.peanut12}`}>🥜</div>
                    <div className={`${styles.fallingPeanut} ${styles.falling1}`}>🥜</div>
                    <div className={`${styles.fallingPeanut} ${styles.falling2}`}>🥜</div>
                    <div className={`${styles.fallingPeanut} ${styles.falling3}`}>🥜</div>
                    <div className={`${styles.fallingPeanut} ${styles.falling4}`}>🥜</div>
                    <div className={`${styles.fallingPeanut} ${styles.falling5}`}>🥜</div>
                    <div className={`${styles.fallingPeanut} ${styles.falling6}`}>🥜</div>
                    <div className={`${styles.fallingPeanut} ${styles.falling7}`}>🥜</div>
                    <div className={`${styles.fallingPeanut} ${styles.falling8}`}>🥜</div>
                </div>

                {/* Header */}
                <header className={styles.header}>
                    <div className={styles.logo}>
                        <div className={styles.logoContainer}>
                            <img src="/logo.png" alt="Kadle Logo" className={styles.logoIcon} />
                            <div className={styles.logoText}>{app_name}</div>
                            <StagingBanner
                                environment={environment}
                                revision={revision}
                                gitlabProjectUrl={gitlab_project_url}
                            />
                        </div>
                        <div className={styles.logoAccent}>🥜</div>
                    </div>
                    <nav className={styles.nav}>
                        <button 
                            className={styles.navButton}
                            onClick={handleLogin}
                        >
                            Login
                        </button>
                    </nav>
                </header>

                {/* Hero Section */}
                <section className={styles.hero}>
                    <div className={styles.heroContent}>
                        <div className={styles.heroText}>
                            <h1 className={styles.heroTitle}>
                                Go Nuts for 
                                <span className={styles.highlight}> Time Tracking</span>!
                                <div className={styles.titlePeanut}>🥜</div>
                            </h1>
                            <p className={styles.heroSubtitle}>
                                Shell-shocked by scattered timesheets? Let's crack open better productivity 
                                with the nuttiest time tracking solution around!
                            </p>
                        </div>
                        
                        <div className={styles.heroActions}>
                            {userOS === 'mac' ? (
                                <button
                                    className={`${styles.ctaButton} ${styles.downloadButton}`}
                                    onClick={() => handleDownload(download_urls.mac_arm)}
                                    disabled={!download_urls?.mac_arm}
                                >
                                    <div className={styles.buttonContent}>
                                        <span className={styles.buttonIcon}>🥜</span>
                                        <div className={styles.buttonText}>
                                            <span className={styles.buttonTitle}>Download for macOS</span>
                                        </div>
                                    </div>
                                    <div className={styles.buttonShell}></div>
                                </button>
                            ) : (
                                <button
                                    className={`${styles.ctaButton} ${styles.downloadButton}`}
                                    onClick={handleDownload}
                                    disabled={!download_urls || Object.keys(download_urls).length === 0}
                                >
                                    <div className={styles.buttonContent}>
                                        <span className={styles.buttonIcon}>🥜</span>
                                        <div className={styles.buttonText}>
                                            <span className={styles.buttonTitle}>Download for {getOSDisplayName()}</span>
                                        </div>
                                    </div>
                                    <div className={styles.buttonShell}></div>
                                </button>
                            )}

                            <button
                                className={`${styles.ctaButton} ${styles.loginButton}`}
                                onClick={handleLogin}
                            >
                                <div className={styles.buttonContent}>
                                    <span className={styles.buttonIcon}>🥜</span>
                                    <div className={styles.buttonText}>
                                        <span className={styles.buttonTitle}>Login</span>
                                    </div>
                                </div>
                            </button>
                        </div>

                        <div className={styles.heroImage}>
                            <div className={styles.heroImageContainer}>
                                {screenshots && screenshots.length > 0 ? (
                                    <ScreenshotCarousel 
                                        screenshots={screenshots}
                                        className={styles.appScreenshots}
                                        autoRotateInterval={3500}
                                    />
                                ) : (
                                    <>
                                        <div className={`${styles.heroPeanut} ${styles.heroPeanut1}`}>🥜</div>
                                        <div className={`${styles.heroPeanut} ${styles.heroPeanut2}`}>🥜</div>
                                        <div className={`${styles.heroPeanut} ${styles.heroPeanut3}`}>🥜</div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className={styles.features}>
                    <div className={styles.featuresContainer}>
                        <h2 className={styles.featuresTitle}>
                            Why Go Nuts for {app_name}?
                            <span className={styles.titleAccent}>🥜</span>
                        </h2>
                        
                        <div className={styles.featuresGrid}>
                            <div className={styles.featureCard}>
                                <div className={styles.featureIcon}>
                                    <span className={styles.featureEmoji}>🥜</span>
                                </div>
                                <h3 className={styles.featureTitle}>Shell-to-Shell Sync</h3>
                                <p className={styles.featureDescription}>
                                    Seamlessly sync your time tracking across desktop and web. 
                                    Your data follows you everywhere!
                                </p>
                            </div>

                            <div className={styles.featureCard}>
                                <div className={styles.featureIcon}>
                                    <span className={styles.featureEmoji}>📊</span>
                                </div>
                                <h3 className={styles.featureTitle}>Crunch the Numbers</h3>
                                <p className={styles.featureDescription}>
                                    Powerful analytics and reporting to help you understand 
                                    where your time really goes.
                                </p>
                            </div>

                            <div className={styles.featureCard}>
                                <div className={styles.featureIcon}>
                                    <span className={styles.featureEmoji}>👥</span>
                                </div>
                                <h3 className={styles.featureTitle}>Team Nutwork</h3>
                                <p className={styles.featureDescription}>
                                    Collaborate with your team, share projects, and track 
                                    collective productivity.
                                </p>
                            </div>

                            <div className={styles.featureCard}>
                                <div className={styles.featureIcon}>
                                    <span className={styles.featureEmoji}>⚡</span>
                                </div>
                                <h3 className={styles.featureTitle}>Simple as Peanuts</h3>
                                <p className={styles.featureDescription}>
                                    Intuitive interface that gets out of your way so you can 
                                    focus on what matters most.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className={styles.footer}>
                    <div className={styles.footerContent}>
                        <div className={styles.footerLogo}>
                            <img src="/logo.png" alt="Kadle Logo" className={styles.footerLogoIcon} />
                            <span className={styles.footerLogoText}>{app_name}</span>
                        </div>
                        <div className={styles.footerText}>
                            <p>Made with 🥜 and ❤️</p>
                            <p className={styles.version}>Version {version}</p>
                        </div>
                    </div>
                </footer>
            </div>
        </>
    )
}